// GENERATED from backend/services/billMemory/core — do not edit; run backend/scripts/sync-bill-memory-core.js
/**
 * Bill Memory — the ladder's context from the plain JSON the server sends (design §11,
 * GET /api/bill-memory/context). The server builds its own context through this same function,
 * so the screen and the server answer a typed row identically. Pure.
 */
import { buildDictionaryIndex } from './dictionary';
import { buildInventoryIndex } from './inventoryIndex';

/** The same normalising as the server's unit table (utils/unitFamilies): lower case, no dots or spaces. */
const normUnit = (u) => String(u == null ? '' : u).toLowerCase().replace(/[.\s]+/g, '').trim();

/** familyOf for a { unit → family } map. */
function familyFromMap(map) {
  const m = map || {};
  return (u) => {
    const n = normUnit(u);
    return (n && Object.prototype.hasOwnProperty.call(m, n) && m[n]) || null;
  };
}

/** payload (the context route's JSON) → the ladder's context, or null while Bill Memory is Off. */
function contextFromPayload(p) {
  if (!p || !p.switches || !p.switches.mode || p.switches.mode === 'off') return null;
  const familyOf = familyFromMap(p.unit_families);
  const dictMode = p.switches.dictionary || 'off';
  const loaded = dictMode !== 'off' && p.dictionary && Array.isArray(p.dictionary.groups)
    ? buildDictionaryIndex(p.dictionary.groups, p.dictionary.optout || null) : null;
  // Only an On dictionary shapes the applied answer (keys, twins, check 5); a Shadow one is kept apart.
  const dictIndex = dictMode === 'on' ? loaded : null;
  const lessons = p.lessons || {};
  const vendor = new Map();
  for (const l of [...(lessons.vendor || []), ...(p.notStock || [])]) vendor.set(l.match_name, l);
  const shop = new Map((lessons.shop || []).map((l) => [l.match_name, l]));
  const v = p.vendor || {};
  const ctx = {
    vendor: { certain: !!v.certain, vendor_key: v.certain ? v.vendor_key || null : null, name: v.certain ? v.name || null : null },
    lessons: { vendor, shop },
    supply: Array.isArray(p.supply) ? new Map(p.supply.map(([id, n]) => [Number(id), Number(n)])) : null,
    invIndex: buildInventoryIndex(p.inventory || [], dictIndex, familyOf),
    dictIndex,
    // Fix 1: lesson sources by their own state; an older payload without them means On.
    switches: { vendorRule: p.switches.vendorRule || 'off', dictionary: dictMode,
      vendorLessons: p.switches.vendorLessons || 'on', shopLessons: p.switches.shopLessons || 'on', quantity: p.switches.quantity || 'on' },
    familyOf,
  };
  if (dictMode === 'shadow' && loaded) {
    ctx.dictionaryShadow = { dictIndex: loaded, invIndex: buildInventoryIndex(p.inventory || [], loaded, familyOf) };
  }
  return ctx;
}

const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** A typed row as a ladder line: exactly what was typed, with no help from today's matcher. */
function typedLineOf(row, lineNo = 1) {
  return {
    line_no: lineNo,
    printed_name: String(row.bm_printed_name || row.item_name || '').trim(),
    original_name: null,
    unit: row.unit || '',
    qty: Number(row.quantity),
    unit_price: num(row.unit_price),
    total_price: num(row.total_price),
    pack: null,
    input_kind: 'typed',
    settled: false,
    item_id: null,
    today: { item_id: null, status: 'none', suggestions: [] },
    ai_rejected: !!row.bm_ai_rejected,
  };
}

/** Today's matcher answer for a route row, as the ladder reads it. */
function todayOf(row) {
  return {
    item_id: row.item_id || null,
    status: row.matchStatus === 'exact' ? 'exact' : row.item_id ? 'fuzzy' : 'none',
    suggestions: Array.isArray(row.smmf_suggestions) ? row.smmf_suggestions : [],
  };
}

/** What a scan keeps of today's matcher answer (bm_lines and the row's bm_today): enough to ask the ladder again. */
function todayRecord(today) {
  const t = today || {};
  return {
    item_id: t.item_id == null ? null : Number(t.item_id),
    status: t.status || 'none',
    suggestions: (t.suggestions || []).map((s) => ({ item_id: s.item_id == null ? null : Number(s.item_id), name: s.name || '' })),
  };
}

/**
 * A scanned (or Excel / pasted) row as a ladder line. The server asks with it at scan and at Save,
 * and the screen when a vendor tap re-asks a row, so all three see the same line. `today` is the
 * scan's own matcher answer when known — the Save payload no longer carries it.
 */
function ladderLineOf(row, lineNo, { inputKind = 'scanned', printedName = '', settled = false, today = null } = {}) {
  const ps = Number(row.pack_size);
  return {
    line_no: lineNo,
    printed_name: printedName,
    original_name: row.original_name || null,
    // The stock unit after pack expansion ("6 pcs" of 1 kg → g); the printed unit only when the route
    // could not name one ("3 bundles" → '') — quantity memory needs it there (D17).
    unit: row.unit || row.ocr_extracted_unit || '',
    qty: Number(row.quantity),
    unit_price: num(row.unit_price),
    total_price: num(row.total_price),
    pack: ps > 0 ? { size: ps, unit: row.pack_unit || null } : null,
    input_kind: inputKind,
    settled,
    item_id: row.item_id == null ? null : Number(row.item_id),
    today: today || todayOf(row),
    ai_rejected: !!row.bm_ai_rejected,
  };
}

export { normUnit, familyFromMap, contextFromPayload, typedLineOf, todayOf, todayRecord, ladderLineOf };
